// The phone Coach's 1 Hz recorder — spans the whole session (count-in → end).
//
// Mounted once in App, beside the views, not inside the Coach: the workout
// keeps running while you look at another tab (ActiveWorkoutBanner), and a
// recorder in the Coach view stopped with it and started a second recording
// on your return. The save only cleared the second, so the first came back as
// an "interrupted workout" duplicating the session you'd saved.
//
// Its finalize + clear-after-save are bound into the WorkoutContext refs so
// the persistCompletedSession path can pluck them at save time without a
// circular import.

import React, { useContext, useEffect } from "react";
import { AppStateContext, WorkoutContext } from "../../contexts/WorkoutContext";
import { useLiveSession } from "../../contexts/SocialContext";
import useTelemetryRecorder, { useTelemetryStepMarks } from "../../hooks/useTelemetryRecorder";
import { heartRateBasis } from "../../utils/heartRateZones";

const CoachTelemetry = () => {
  const { appState, coachState } = useContext(AppStateContext);
  const {
    userProfile,
    telemetryFinalizeRef,
    telemetryClearAfterSaveRef,
    telemetryPeekRef,
  } = useContext(WorkoutContext);
  // A live session (or one sent to your timer) records in LiveSessionFlow.
  const live = !!useLiveSession()?.liveSession?.session;
  // Heart-rate zones (#94) from the Profile's max and resting heart rate.
  const hrBasis = React.useMemo(() => heartRateBasis(userProfile), [userProfile]);
  const telemetry = useTelemetryRecorder({
    active: appState === "active" && !live,
    hrBasis,
  });
  useTelemetryStepMarks(telemetry.mark, coachState);

  const { finalize, clearAfterSave, release, samplesRef } = telemetry;
  useEffect(() => {
    if (!telemetryPeekRef) return undefined;
    telemetryPeekRef.current = () => samplesRef.current;
    return () => {
      telemetryPeekRef.current = null;
    };
  }, [samplesRef, telemetryPeekRef]);
  useEffect(() => {
    if (telemetryFinalizeRef) telemetryFinalizeRef.current = finalize;
    if (telemetryClearAfterSaveRef) telemetryClearAfterSaveRef.current = clearAfterSave;
    return () => {
      if (telemetryFinalizeRef) telemetryFinalizeRef.current = null;
      if (telemetryClearAfterSaveRef) telemetryClearAfterSaveRef.current = null;
    };
  }, [finalize, clearAfterSave, telemetryFinalizeRef, telemetryClearAfterSaveRef]);

  // Back at rest without a save (the session cleared, or handed to a live
  // session): the next workout gets its own recording.
  const atRest = appState !== "active" && appState !== "finished";
  useEffect(() => {
    if (atRest) release();
  }, [atRest, release]);

  return null;
};

export default CoachTelemetry;
